import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  LayoutDashboard, 
  Eye, 
  TrendingDown, 
  ListTodo, 
  Package, 
  History, 
  Wallet, 
  Users, 
  FileSpreadsheet,
  Clock,
  Building2,
  Settings,
  Calendar,
  CheckCircle,
  AlertCircle,
  DollarSign,
  ArrowRight,
  LogOut,
  BarChart3,
  Tag,
  Wine,
  UtensilsCrossed,
  Coffee,
  Boxes,
  Warehouse,
  BookOpen,
  GlassWater,
  ClipboardCheck,
  RefreshCw,
  GripVertical,
  Edit3,
  Save,
  X,
  RotateCcw,
  TrendingUp,
  Zap,
  Landmark,
  Scale,
  ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Import all the components
import ViewDayClosings from './ViewDayClosings';
import ExpenseLedger from './ExpenseLedger';
import ExpensePipeline from './ExpensePipeline';
import CreditLedger from './CreditLedger';
import OpeningLiabilities from './OpeningLiabilities';
import PayBills from './PayBills';
import SalaryMaster from './SalaryMaster';
import PayrollReport from './PayrollReport';
import VendorAgingReport from './VendorAgingReport';
import BankManagement from './BankManagement';
import SettingsPage from './Settings';
import Reports from './Reports';
import PriceMaster from './PriceMaster';
import BarMaster from './BarMaster';
import KitchenMaster from './KitchenMaster';
import CafeMaster from './CafeMaster';
import NAPriceMaster from './NAPriceMaster';
import StoreLedger from './StoreLedger';
import RecipeLedger from './RecipeLedger';
import CrockeryAudit from './CrockeryAudit';
import AccountantAudit from './AccountantAudit';
import CEOAudit from './CEOAudit';
import CashFlowStatement from './CashFlowStatement';
import CEODashboard from './CEODashboard';
import ReconciliationDashboard from './ReconciliationDashboard';
import UtilityLedger from './UtilityLedger';
import ClosingsCalendar from './ClosingsCalendar';
import FraudAlerts from './FraudAlerts';
import LoansLedger from './LoansLedger';
import OrphanedEntries from './OrphanedEntries';

import { BACKEND_URL } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
const API = `${BACKEND_URL}/api`;

// Sortable Tab Item Component
const SortableTabItem = ({ tab, isActive, onClick, isEditMode }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  const Icon = tab.icon;

  return (
    <div ref={setNodeRef} style={style}>
      <button
        onClick={() => !isEditMode && onClick()}
        className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all w-full ${
          isActive 
            ? 'bg-blue-600 text-white shadow-md' 
            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
        } ${isEditMode ? 'cursor-grab active:cursor-grabbing' : ''}`}
        {...(isEditMode ? { ...attributes, ...listeners } : {})}
      >
        {isEditMode && <GripVertical className="w-4 h-4 text-gray-400" />}
        <Icon className="w-4 h-4" />
        {tab.label}
      </button>
    </div>
  );
};

// Map tab IDs to permission module names
const TAB_TO_MODULE = {
  'dashboard': 'dashboard',
  'ceo-dashboard': 'ceo_dashboard',
  'ceo-audit': 'ceo_audit',
  'audit-checklist': 'accountant_audit',
  'reports': 'reports',
  'cash-flow-statement': 'cash_flow',
  'reconciliation': 'reconciliation',  // Admin-only
  'loans-ledger': 'loans',
  'view-closings': 'view_closings',
  'closings-calendar': 'closings_calendar',
  'expense-ledger': 'expense_ledger',
  'expense-pipeline': 'expense_pipeline',
  'credit-ledger': 'credit_ledger',
  'price-master': 'price_master',
  'na-price-master': 'na_price_master',
  'store-ledger': 'store_ledger',
  'recipe-ledger': 'recipe_ledger',
  'crockery': 'crockery_audit',
  'bar-master': 'bar_master',
  'kitchen-master': 'kitchen_master',
  'cafe-master': 'cafe_master',
  'liabilities': 'opening_liabilities',
  'utility-ledger': 'utility_ledger',
  'fraud-alerts': 'fraud_alerts',
  'orphaned-entries': 'orphaned_entries',
  'pay-bills': 'pay_bills',
  'salary': 'salary_master',
  'payroll': 'payroll_report',
  'vendor-aging': 'vendor_aging',
  'banks': 'bank_management',
  'settings': 'settings',
};

const AdminDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({
    pendingClosings: 0,
    pendingBills: 0,
    pendingSalaries: 0,
    totalPending: 0
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [recentClosings, setRecentClosings] = useState([]);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [userPermissions, setUserPermissions] = useState({});
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [layoutLoaded, setLayoutLoaded] = useState(false);
  
  // Layout management state
  const [tileOrder, setTileOrder] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTileOrder, setEditingTileOrder] = useState([]);
  const [editScope, setEditScope] = useState('my'); // 'my', 'global', 'user:{id}', 'role:{name}'
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [savingLayout, setSavingLayout] = useState(false);
  const [resettingLayout, setResettingLayout] = useState(false);

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Fetch layout with retry logic - never fall back to defaults
  const fetchLayout = useCallback(async (retryCount = 0) => {
    const maxRetries = 5;
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found');
        // Keep retrying until token is available
        setTimeout(() => fetchLayout(0), 2000);
        return;
      }
      // Add cache-busting param to ensure fresh layout after deployments
      const response = await axios.get(`${API}/dashboard-layout/me?t=${Date.now()}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache'
        },
        timeout: 10000 // 10 second timeout
      });
      setTileOrder(response.data.tile_order || []);
      setLayoutLoaded(true);
    } catch (error) {
      console.error('Failed to fetch layout (attempt ' + (retryCount + 1) + '):', error);
      if (retryCount < maxRetries) {
        // Retry after delay (exponential backoff)
        setTimeout(() => fetchLayout(retryCount + 1), 1000 * (retryCount + 1));
      } else {
        // Keep retrying every 5 seconds - never give up
        toast.error('Failed to load your dashboard layout. Retrying...');
        setTimeout(() => fetchLayout(0), 5000);
      }
    }
  }, []);

  // Fetch scopes for admin
  const fetchScopes = useCallback(async () => {
    if (user?.role !== 'admin') return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard-layout/scopes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableUsers(response.data.users || []);
      setAvailableRoles(response.data.roles || []);
    } catch (error) {
      console.error('Failed to fetch scopes:', error);
    }
  }, [user?.role]);

  useEffect(() => {
    fetchLayout();
    fetchScopes();
  }, [fetchLayout, fetchScopes]);

  // Fetch user permissions
  useEffect(() => {
    const fetchPermissions = async () => {
      // Admin has all permissions
      if (user?.role === 'admin') {
        setUserPermissions({});
        setPermissionsLoaded(true);
        return;
      }
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/user-access/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUserPermissions(response.data.effective_permissions || {});
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
        setUserPermissions({});
      } finally {
        setPermissionsLoaded(true);
      }
    };
    
    fetchPermissions();
  }, [user?.role]);

  // Check if user has access to a tab
  const hasAccess = (tabId) => {
    // Admin has full access
    if (user?.role === 'admin') return true;
    
    const moduleName = TAB_TO_MODULE[tabId];
    if (!moduleName) return true; // If no mapping, allow access
    
    return userPermissions[moduleName] === true;
  };

  const fetchDashboardData = useCallback(async (showLoader = true, retryCount = 0) => {
    const maxRetries = 3;
    if (showLoader) setLoading(true);
    setRefreshing(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found for dashboard data');
        return;
      }
      
      // Fetch multiple data points in parallel with timeout
      const [closingsRes, pipelineRes, salariesRes] = await Promise.all([
        axios.get(`${API}/dayclosings`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { 
            start_date: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            end_date: new Date().toISOString().split('T')[0]
          },
          timeout: 15000
        }).catch(() => ({ data: [] })),
        axios.get(`${API}/expense-pipeline`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000
        }).catch(() => ({ data: { summary: {} } })),
        axios.get(`${API}/salaries`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: 'payable' },
          timeout: 15000
        }).catch(() => ({ data: { summary: {} } }))
      ]);
      
      const closingsData = Array.isArray(closingsRes.data) ? closingsRes.data : [];
      const pendingClosings = closingsData.filter(c => c.status === 'closed').length;
      const pendingBills = pipelineRes.data?.summary?.total_items || 0;
      const totalPending = pipelineRes.data?.summary?.total_pending || 0;
      const pendingSalaries = salariesRes.data?.summary?.pending_count || 0;
      
      setStats({
        pendingClosings,
        pendingBills,
        pendingSalaries,
        totalPending
      });
      
      setRecentClosings(closingsData.slice(0, 5));
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      // Retry on network errors
      if (retryCount < maxRetries && (error.code === 'ECONNABORTED' || error.message?.includes('Network Error'))) {
        setTimeout(() => fetchDashboardData(showLoader, retryCount + 1), 1000 * (retryCount + 1));
        return;
      }
      toast.error('Failed to load dashboard data. Please refresh the page.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  // Set default tab to first accessible one when permissions load
  useEffect(() => {
    if (!permissionsLoaded) return;
    
    // If current tab is not accessible, switch to first accessible tab
    if (!hasAccess(activeTab)) {
      const firstAccessible = tabItems.find(tab => hasAccess(tab.id));
      if (firstAccessible) {
        setActiveTab(firstAccessible.id);
      }
    }
  }, [permissionsLoaded, userPermissions]);

  // Auto-refresh every 45 seconds when on dashboard tab
  useEffect(() => {
    if (activeTab !== 'dashboard') return;
    
    const interval = setInterval(() => {
      fetchDashboardData(false); // Silent refresh (no loader)
    }, 45000); // 45 seconds

    return () => clearInterval(interval);
  }, [activeTab, fetchDashboardData]);

  // Refresh data when switching back to dashboard tab
  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboardData(false);
    }
  }, [activeTab, fetchDashboardData]);

  const handleManualRefresh = () => {
    fetchDashboardData(false);
    toast.success('Dashboard refreshed!');
  };

  // Layout management functions
  const startEditMode = () => {
    setEditingTileOrder([...tileOrder]);
    setIsEditMode(true);
    setEditScope('my');
  };

  const cancelEditMode = () => {
    setIsEditMode(false);
    setEditingTileOrder([]);
    setEditScope('my');
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      setEditingTileOrder((items) => {
        const oldIndex = items.indexOf(active.id);
        const newIndex = items.indexOf(over.id);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const saveLayout = async () => {
    if (savingLayout) return; // Prevent double-click
    
    setSavingLayout(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      if (editScope === 'my') {
        endpoint = `${API}/dashboard-layout/user/${user.id}`;
      } else if (editScope === 'global') {
        endpoint = `${API}/dashboard-layout/global`;
      } else if (editScope.startsWith('user:')) {
        const userId = editScope.replace('user:', '');
        endpoint = `${API}/dashboard-layout/user/${userId}`;
      } else if (editScope.startsWith('role:')) {
        const roleName = editScope.replace('role:', '');
        endpoint = `${API}/dashboard-layout/role/${roleName}`;
      }

      await axios.put(endpoint, { tile_order: editingTileOrder }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Layout saved successfully!');
      setIsEditMode(false);
      
      // Refresh layout if editing own layout
      if (editScope === 'my' || editScope === `user:${user.id}`) {
        setTileOrder(editingTileOrder);
      }
    } catch (error) {
      toast.error('Failed to save layout');
      console.error(error);
    } finally {
      setSavingLayout(false);
    }
  };

  const resetLayout = async () => {
    if (resettingLayout) return; // Prevent double-click
    if (!window.confirm('Are you sure you want to reset this layout to default?')) return;
    
    try {
      setResettingLayout(true);
      const token = localStorage.getItem('token');
      let scopeType, scopeId;

      if (editScope === 'my') {
        scopeType = 'user';
        scopeId = user.id;
      } else if (editScope === 'global') {
        scopeType = 'global';
        scopeId = 'global';
      } else if (editScope.startsWith('user:')) {
        scopeType = 'user';
        scopeId = editScope.replace('user:', '');
      } else if (editScope.startsWith('role:')) {
        scopeType = 'role';
        scopeId = editScope.replace('role:', '');
      }

      await axios.post(`${API}/dashboard-layout/reset`, 
        { scope_type: scopeType, scope_id: scopeId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Layout reset to default!');
      setIsEditMode(false);
      fetchLayout();
    } catch (error) {
      toast.error('Failed to reset layout');
    } finally {
      setResettingLayout(false);
    }
  };

  const tabItemsMap = {
    'dashboard': { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    'ceo-dashboard': { id: 'ceo-dashboard', label: 'CEO Dashboard', icon: TrendingUp },
    'ceo-audit': { id: 'ceo-audit', label: 'CEO Audit', icon: ShieldCheck },
    'audit-checklist': { id: 'audit-checklist', label: 'Audit', icon: ClipboardCheck },
    'reports': { id: 'reports', label: 'Reports', icon: BarChart3 },
    'cash-flow-statement': { id: 'cash-flow-statement', label: 'Cash Flow', icon: DollarSign },
    'reconciliation': { id: 'reconciliation', label: 'Reconciliation', icon: Scale },
    'loans-ledger': { id: 'loans-ledger', label: 'Loans', icon: Landmark },
    'view-closings': { id: 'view-closings', label: 'View Closings', icon: Eye },
    'closings-calendar': { id: 'closings-calendar', label: 'Calendar', icon: Calendar },
    'expense-ledger': { id: 'expense-ledger', label: 'Expense Ledger', icon: TrendingDown },
    'expense-pipeline': { id: 'expense-pipeline', label: 'Pipeline', icon: ListTodo },
    'credit-ledger': { id: 'credit-ledger', label: 'Credit Ledger', icon: Package },
    'price-master': { id: 'price-master', label: 'Price Master', icon: Tag },
    'na-price-master': { id: 'na-price-master', label: 'NA-Price', icon: Boxes },
    'store-ledger': { id: 'store-ledger', label: 'Store Ledger', icon: Warehouse },
    'recipe-ledger': { id: 'recipe-ledger', label: 'Recipes', icon: BookOpen },
    'crockery': { id: 'crockery', label: 'Crockery', icon: GlassWater },
    'bar-master': { id: 'bar-master', label: 'Bar Master', icon: Wine },
    'kitchen-master': { id: 'kitchen-master', label: 'Kitchen', icon: UtensilsCrossed },
    'cafe-master': { id: 'cafe-master', label: 'Cafe', icon: Coffee },
    'liabilities': { id: 'liabilities', label: 'Liabilities', icon: History },
    'utility-ledger': { id: 'utility-ledger', label: 'Utility Ledger', icon: Zap },
    'fraud-alerts': { id: 'fraud-alerts', label: 'Fraud Alerts', icon: AlertCircle },
    'orphaned-entries': { id: 'orphaned-entries', label: 'Orphaned', icon: AlertCircle },
    'pay-bills': { id: 'pay-bills', label: 'Pay Bills', icon: Wallet },
    'salary': { id: 'salary', label: 'Salary', icon: Users },
    'payroll': { id: 'payroll', label: 'Payroll', icon: FileSpreadsheet },
    'vendor-aging': { id: 'vendor-aging', label: 'Vendor Aging', icon: Clock },
    'banks': { id: 'banks', label: 'Banks', icon: Building2 },
    'settings': { id: 'settings', label: 'Settings', icon: Settings },
  };

  // Build sorted tab items based on tileOrder
  const currentOrder = isEditMode ? editingTileOrder : tileOrder;
  const tabItems = currentOrder
    .filter(id => tabItemsMap[id])
    .map(id => tabItemsMap[id]);

  // Filter tabs based on permissions
  const accessibleTabs = tabItems.filter(tab => hasAccess(tab.id));

  // Show loading while permissions or layout are being fetched
  if (!permissionsLoaded || !layoutLoaded) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading {!permissionsLoaded ? 'permissions' : 'layout'}...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Hoppers Finance DSR</h1>
          <p className="text-sm text-gray-500">Welcome, {user?.name} ({user?.role})</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Edit Layout Button - Admin only */}
          {user?.role === 'admin' && !isEditMode && (
            <Button variant="outline" size="sm" onClick={startEditMode}>
              <Edit3 className="w-4 h-4 mr-2" />
              Edit Layout
            </Button>
          )}
          {onLogout && (
            <Button variant="outline" size="sm" onClick={onLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          )}
        </div>
      </div>

      {/* Edit Mode Controls */}
      {isEditMode && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-yellow-800">
                <Edit3 className="w-4 h-4 inline mr-1" />
                Editing Layout:
              </span>
              <Select value={editScope} onValueChange={setEditScope}>
                <SelectTrigger className="w-48 h-8 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="my">My Layout</SelectItem>
                  <SelectItem value="global">Global Default</SelectItem>
                  {availableUsers.map(u => (
                    <SelectItem key={u.id} value={`user:${u.id}`}>
                      User: {u.name}
                    </SelectItem>
                  ))}
                  {availableRoles.map(role => (
                    <SelectItem key={role} value={`role:${role}`}>
                      Role: {role}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-yellow-600">Drag tiles to reorder</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={resetLayout}>
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </Button>
              <Button variant="outline" size="sm" onClick={cancelEditMode}>
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button size="sm" onClick={saveLayout} disabled={savingLayout}>
                <Save className="w-4 h-4 mr-1" />
                {savingLayout ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs Navigation - Grid Layout */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <div className="bg-white border-b px-6 py-4">
          {isEditMode ? (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext items={editingTileOrder} strategy={rectSortingStrategy}>
                <div className="grid grid-cols-4 gap-2">
                  {editingTileOrder.map((id) => {
                    const tab = tabItemsMap[id];
                    if (!tab) return null;
                    return (
                      <SortableTabItem
                        key={tab.id}
                        tab={tab}
                        isActive={activeTab === tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        isEditMode={true}
                      />
                    );
                  })}
                </div>
              </SortableContext>
            </DndContext>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {accessibleTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      isActive 
                        ? 'bg-blue-600 text-white shadow-md' 
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="mt-0 p-6">
          {loading ? (
            <div className="text-center py-12">Loading dashboard...</div>
          ) : (
            <div className="space-y-6">
              {/* Refresh Bar */}
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-500">
                  Last updated: {lastRefresh.toLocaleTimeString('en-IN')}
                  {refreshing && <span className="ml-2 text-blue-500">• Refreshing...</span>}
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleManualRefresh}
                  disabled={refreshing}
                  className="gap-2"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => setActiveTab('view-closings')}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-100 text-sm">Pending Approvals</p>
                        <p className="text-3xl font-bold mt-1">{stats.pendingClosings}</p>
                        <p className="text-blue-100 text-xs mt-1">Day closings to review</p>
                      </div>
                      <Eye className="w-10 h-10 text-blue-200" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => setActiveTab('expense-pipeline')}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-orange-100 text-sm">Pending Payments</p>
                        <p className="text-3xl font-bold mt-1">{stats.pendingBills}</p>
                        <p className="text-orange-100 text-xs mt-1">{formatCurrency(stats.totalPending)}</p>
                      </div>
                      <ListTodo className="w-10 h-10 text-orange-200" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
                      onClick={() => setActiveTab('salary')}>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-100 text-sm">Pending Salaries</p>
                        <p className="text-3xl font-bold mt-1">{stats.pendingSalaries}</p>
                        <p className="text-green-100 text-xs mt-1">Employees to pay</p>
                      </div>
                      <Users className="w-10 h-10 text-green-200" />
                    </div>
                  </CardContent>
                </Card>

                {hasAccess('settings') && (
                  <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white cursor-pointer hover:shadow-lg transition-shadow"
                        onClick={() => setActiveTab('settings')}>
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-purple-100 text-sm">Quick Action</p>
                          <p className="text-xl font-bold mt-1">Settings</p>
                          <p className="text-purple-100 text-xs mt-1">Manage categories & users</p>
                        </div>
                        <Settings className="w-10 h-10 text-purple-200" />
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Quick Links Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {accessibleTabs.slice(2).map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <Card 
                      key={tab.id}
                      className="cursor-pointer hover:bg-gray-50 hover:shadow-md transition-all"
                      onClick={() => setActiveTab(tab.id)}
                    >
                      <CardContent className="p-4 text-center">
                        <Icon className="w-6 h-6 mx-auto text-gray-600 mb-2" />
                        <p className="text-xs font-medium text-gray-700">{tab.label}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Recent Day Closings */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center justify-between">
                    <span className="flex items-center gap-2">
                      <Calendar className="w-5 h-5" />
                      Recent Day Closings
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => setActiveTab('view-closings')}>
                      View All <ArrowRight className="w-4 h-4 ml-1" />
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {recentClosings.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No recent closings</p>
                  ) : (
                    <div className="space-y-2">
                      {recentClosings.map((closing) => (
                        <div key={closing.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <div className={`p-2 rounded-full ${
                              closing.status === 'approved' ? 'bg-green-100' :
                              closing.status === 'closed' ? 'bg-yellow-100' : 'bg-gray-100'
                            }`}>
                              {closing.status === 'approved' ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : closing.status === 'closed' ? (
                                <Clock className="w-4 h-4 text-yellow-600" />
                              ) : (
                                <AlertCircle className="w-4 h-4 text-gray-600" />
                              )}
                            </div>
                            <div>
                              <p className="font-medium">{closing.date}</p>
                              <p className="text-xs text-gray-500">{closing.cashier_name}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{formatCurrency(closing.total_sales)}</p>
                            <Badge variant={closing.status === 'approved' ? 'default' : 'secondary'} className="text-xs">
                              {closing.status.toUpperCase()}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>

        {/* Reports Tab */}
        <TabsContent value="reports" className="mt-0">
          <Reports />
        </TabsContent>

        {/* Cash Flow Statement Tab */}
        <TabsContent value="cash-flow-statement" className="mt-0">
          <CashFlowStatement user={user} />
        </TabsContent>

        {/* Reconciliation Tab - Admin Only */}
        <TabsContent value="reconciliation" className="mt-0">
          <ReconciliationDashboard user={user} />
        </TabsContent>

        {/* Loans Ledger Tab */}
        <TabsContent value="loans-ledger" className="mt-0">
          <LoansLedger user={user} />
        </TabsContent>

        {/* Audit Checklist Tab */}
        <TabsContent value="audit-checklist" className="mt-0">
          <AccountantAudit user={user} />
        </TabsContent>

        {/* View Closings Tab */}
        <TabsContent value="view-closings" className="mt-0">
          <ViewDayClosings user={user} />
        </TabsContent>

        {/* Closings Calendar Tab */}
        <TabsContent value="closings-calendar" className="mt-0">
          <ClosingsCalendar user={user} />
        </TabsContent>

        {/* Expense Ledger Tab */}
        <TabsContent value="expense-ledger" className="mt-0">
          <ExpenseLedger user={user} />
        </TabsContent>

        {/* Expense Pipeline Tab */}
        <TabsContent value="expense-pipeline" className="mt-0">
          <ExpensePipeline user={user} />
        </TabsContent>

        {/* Credit Ledger Tab */}
        <TabsContent value="credit-ledger" className="mt-0">
          <CreditLedger user={user} />
        </TabsContent>

        {/* Price Master Tab */}
        <TabsContent value="price-master" className="mt-0">
          <PriceMaster user={user} />
        </TabsContent>

        {/* NA-Price Master Tab */}
        <TabsContent value="na-price-master" className="mt-0">
          <NAPriceMaster user={user} />
        </TabsContent>

        {/* Store Ledger Tab */}
        <TabsContent value="store-ledger" className="mt-0">
          <StoreLedger user={user} />
        </TabsContent>

        {/* Recipe Ledger Tab */}
        <TabsContent value="recipe-ledger" className="mt-0">
          <RecipeLedger user={user} />
        </TabsContent>

        {/* Crockery Audit Tab */}
        <TabsContent value="crockery" className="mt-0">
          <CrockeryAudit user={user} />
        </TabsContent>

        {/* Bar Master Tab */}
        <TabsContent value="bar-master" className="mt-0">
          <BarMaster user={user} />
        </TabsContent>

        {/* Kitchen Master Tab */}
        <TabsContent value="kitchen-master" className="mt-0">
          <KitchenMaster user={user} />
        </TabsContent>

        {/* Cafe Master Tab */}
        <TabsContent value="cafe-master" className="mt-0">
          <CafeMaster user={user} />
        </TabsContent>

        {/* Opening Liabilities Tab */}
        <TabsContent value="liabilities" className="mt-0">
          <OpeningLiabilities user={user} />
        </TabsContent>

        {/* Utility Ledger Tab */}
        <TabsContent value="utility-ledger" className="mt-0">
          <UtilityLedger user={user} />
        </TabsContent>

        {/* Fraud Alerts Tab */}
        <TabsContent value="fraud-alerts" className="mt-0">
          <FraudAlerts user={user} />
        </TabsContent>

        {/* Orphaned Entries Tab */}
        <TabsContent value="orphaned-entries" className="mt-0">
          <OrphanedEntries user={user} />
        </TabsContent>

        {/* Pay Bills Tab */}
        <TabsContent value="pay-bills" className="mt-0">
          <PayBills user={user} />
        </TabsContent>

        {/* Salary Master Tab */}
        <TabsContent value="salary" className="mt-0">
          <SalaryMaster user={user} />
        </TabsContent>

        {/* Payroll Report Tab */}
        <TabsContent value="payroll" className="mt-0">
          <PayrollReport user={user} />
        </TabsContent>

        {/* Vendor Aging Tab */}
        <TabsContent value="vendor-aging" className="mt-0">
          <VendorAgingReport user={user} />
        </TabsContent>

        {/* Bank Management Tab */}
        <TabsContent value="banks" className="mt-0">
          <BankManagement user={user} />
        </TabsContent>

        {/* Settings Tab */}
        <TabsContent value="settings" className="mt-0">
          <SettingsPage user={user} />
        </TabsContent>

        {/* CEO Dashboard Tab */}
        <TabsContent value="ceo-dashboard" className="mt-0">
          <CEODashboard user={user} />
        </TabsContent>

        {/* CEO Audit Tab */}
        <TabsContent value="ceo-audit" className="mt-0">
          <CEOAudit />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminDashboard;
